import React from "react";

import {alpha, Button} from "@mui/material";
import type {ButtonProps} from "@mui/material";
import {OpenInNew, PlayArrow} from "@vedmarket/ui-kit";
import cx from "classnames";
import styles from "./Buttons.module.scss";
import {COLORS} from "../../../../../config/constants";
import ButtonLabel from "../typography/ButtonLabel";
import {DEMO_LINK} from "../../constants";
import {MOBILE_BUTTON_PADDING} from './appearance';
import type {ButtonAppearanceProps} from './appearance';

type OutlinedButtonProps = ButtonProps & ButtonAppearanceProps & {
    children: React.ReactNode;
    isBig?: boolean;
    icon?: 'play' | 'open' | null;
    isBlack?: boolean;
    subduedMobileBorder?: boolean;
    solidTabletBorder?: boolean;
};

function OutlinedButton({
    children, icon = 'play', isBlack, isBig, className, sx, onClick,
    mobileSpacing = 'standard', fixedMobileHeight, comfortableLabel, compactDesktop,
    subduedMobileBorder, solidTabletBorder, ...props
}: OutlinedButtonProps) {
    const ButtonIcon = icon === 'open' ? OpenInNew : PlayArrow;
    const mobileWhiteBorder = subduedMobileBorder ? alpha(COLORS.WHITE, 0.56) : COLORS.WHITE;
    const mobileBorder = isBlack ? mobileWhiteBorder : COLORS.PRIMARY_BORDER;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);

        if (!event.defaultPrevented) {
            window.open(DEMO_LINK, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <Button variant='outlined' sx={[
            {
                '&.MuiButton-outlined': {
                    border: `1px solid ${isBlack ? COLORS.WHITE : COLORS.PRIMARY}`,
                    color: isBlack ? COLORS.WHITE : COLORS.PRIMARY,
                    '& .MuiTouchRipple-root': {
                        color: COLORS.WHITE,
                    },
                    '&:hover': {
                        background: isBlack ? 'transparent' : COLORS.PRIMARY_HOVER_LIGHT,
                        color: isBlack ? COLORS.WHITE : COLORS.PRIMARY,
                    },
                    '@media (min-width: 1200px)': {
                        padding: '0 24px',
                    },
                },
            },
            {
                '@media screen and (min-width: 576px)': {
                    '&.MuiButton-outlined': {
                        borderColor: isBlack ? alpha(COLORS.WHITE, 0.56) : COLORS.PRIMARY_BORDER,
                    },
                },
                '@media screen and (max-width: 575px)': {
                    height: fixedMobileHeight ? '40px' : undefined,
                    minHeight: fixedMobileHeight ? '40px' : 0,
                    padding: MOBILE_BUTTON_PADDING[mobileSpacing],
                    '&.MuiButton-outlined': {
                        borderWidth: 0,
                        boxShadow: `inset 0 0 0 1px ${mobileBorder}`,
                        '&:hover, &:active': {
                            borderWidth: 0,
                        },
                    },
                },
                '@media (min-width: 576px) and (max-width: 767px)': {
                    '&.MuiButton-outlined': {
                        borderColor: solidTabletBorder ? COLORS.WHITE : undefined,
                    },
                },
                '@media (min-width: 1200px)': {
                    height: compactDesktop ? '40px' : undefined,
                    minHeight: compactDesktop ? '40px' : undefined,
                },
            },
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
                className={cx(styles.button, className, {
                    [styles.buttonBig]: isBig,
                })}
                onClick={handleClick}
                {...props}
        >
            {icon && (
                <span className={styles.iconMask} aria-hidden="true">
                    <ButtonIcon className={styles.icon}/>
                </span>
            )}
            <ButtonLabel comfortable={comfortableLabel}>{children}</ButtonLabel>
        </Button>
    );
}

export default OutlinedButton
